/**
 * Outil « Jetons + et − » (Boîte à outils, 5e → 3e).
 *
 * Additionner : on pose les jetons de chaque terme, les paires (+1 ; −1)
 * valent 0 et s'en vont, il reste le résultat ; vue « droite graduée » en
 * sauts. Soustraire : ENLEVER des jetons, après avoir ajouté des paires
 * nulles s'il en manque — enlever des −, c'est ajouter des + (l'opposé).
 * Dès la 4e, multiplier : a groupes de b jetons, ou pour un facteur négatif
 * enlever des groupes à partir de zéro ; signe d'un produit de plusieurs
 * facteurs. Droite graduée : comparer, opposés, distance à zéro.
 * Calculs : `counters.ts`.
 */

import { useState, type ReactElement } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  NumberField,
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { arrowHeadPoints, FIGURE_BG_CLASS, HaloText, INK, MUTED } from '../../ui/svg-parts';
import {
  compareRelatives,
  fmt,
  oppositeTex,
  productScenes,
  productSign,
  regroupTex,
  relTex,
  signed,
  signedTex,
  simplifiedTex,
  spoken,
  subScenes,
  sumScenes,
  sceneWidth,
  type BlockScene,
  type PairScene,
  type Sign,
} from './counters';

type Mode = 'add' | 'sub' | 'mul' | 'line';

const PLUS_C = 'var(--color-primary-500)';
const MINUS_C = 'var(--color-error-500)';
const ADDED_C = 'var(--color-success-600)';
const VB_W = 340;

const atomsOf = (n: number) => (n < 0 ? ['−', fmt(-n)] : [fmt(n)]);

export function SignedCountersTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = Math.min(4, Math.max(2, schoolLevelRank(level) ?? 2));
  const [mode, setMode] = useState<Mode>('add');
  const options: Array<{ value: Mode; label: string }> = [
    { value: 'add', label: 'Additionner' },
    { value: 'sub', label: 'Soustraire' },
    ...(rank >= 3 ? [{ value: 'mul' as const, label: 'Multiplier' }] : []),
    { value: 'line', label: 'Droite graduée' },
  ];
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Mode" value={mode} options={options} onChange={setMode} />
      <div hidden={mode !== 'add'} className="space-y-4">
        <AddMode onSend={onSend} sendCost={sendCost} />
      </div>
      <div hidden={mode !== 'sub'} className="space-y-4">
        <SubMode onSend={onSend} sendCost={sendCost} />
      </div>
      {rank >= 3 && (
        <div hidden={mode !== 'mul'} className="space-y-4">
          <MulMode onSend={onSend} sendCost={sendCost} />
        </div>
      )}
      <div hidden={mode !== 'line'} className="space-y-4">
        <LineMode />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* commandes communes                                                  */
/* ------------------------------------------------------------------ */

function RelStepper({
  label,
  value,
  onChange,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max: number;
}) {
  return (
    <Stepper label={label} value={value} min={-max} max={max} onChange={onChange} format={signed} />
  );
}

/** étape courante, remise à 0 quand le calcul change */
function useSteps(key: string, count: number) {
  const [st, setSt] = useState({ key, step: 0 });
  const step = st.key === key ? Math.min(st.step, count - 1) : 0;
  return {
    step,
    last: step === count - 1,
    next: () => setSt({ key, step: Math.min(step + 1, count - 1) }),
    restart: () => setSt({ key, step: 0 }),
  };
}

function StepControls({
  say,
  last,
  next,
  restart,
  testId,
}: {
  say: string;
  last: boolean;
  next: () => void;
  restart: () => void;
  testId: string;
}) {
  return (
    <div className="space-y-2">
      <Note>
        <span data-testid={testId}>{frenchSpaces(say)}</span>
      </Note>
      {/* un seul bouton qui change de rôle : le focus reste dessus */}
      <button
        type="button"
        className={last ? SECONDARY_BUTTON : PRIMARY_BUTTON}
        onClick={last ? restart : next}
      >
        {last ? 'Recommencer' : 'Étape suivante'}
      </button>
    </div>
  );
}

function SendInt({
  value,
  onSend,
  sendCost,
}: {
  value: number;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  if (!onSend) return null;
  return (
    <SendToSheetButton
      label={signed(value).replace('+', '')}
      cost={sendCost}
      onClick={() => onSend({ atoms: atomsOf(value) })}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Additionner                                                         */
/* ------------------------------------------------------------------ */

function AddMode({ onSend, sendCost }: ToolProps) {
  const [terms, setTerms] = useState<number[]>([5, -3]);
  const [view, setView] = useState<'tokens' | 'line'>('tokens');
  const r = sumScenes(terms);
  const s = useSteps(terms.join(','), r.scenes.length);
  const scene = r.scenes[s.step]!;
  const regroup = regroupTex(terms);
  const setTerm = (i: number, v: number) => setTerms((t) => t.map((x, j) => (j === i ? v : x)));
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {terms.map((t, i) => (
          <RelStepper
            key={i}
            label={`Terme ${i + 1}`}
            value={t}
            max={9}
            onChange={(v) => setTerm(i, v)}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {terms.length < 4 && (
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => setTerms((t) => [...t, -2])}
          >
            Ajouter un terme
          </button>
        )}
        {terms.length > 2 && (
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => setTerms((t) => t.slice(0, -1))}
          >
            Retirer le dernier
          </button>
        )}
      </div>
      <MathText className="text-ink-900 block overflow-x-auto text-xl">
        {`$${terms.map(relTex).join(' + ')}$`}
      </MathText>
      <Segmented
        ariaLabel="Vue"
        value={view}
        options={[
          { value: 'tokens', label: 'Jetons' },
          { value: 'line', label: 'Sauts sur la droite' },
        ]}
        onChange={setView}
      />
      {view === 'tokens' ? (
        <>
          <PairFigure scene={scene} most={Math.max(...r.scenes.map(sceneWidth))} />
          <StepControls say={scene.say} {...s} testId="add-say" />
        </>
      ) : (
        <JumpFigure terms={terms} />
      )}
      {(s.last || view === 'line') && (
        <section
          className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
          data-testid="add-result"
        >
          <MathText className="text-ink-900 block overflow-x-auto text-lg">
            {`$${regroup ?? `${terms.map(relTex).join(' + ')} = ${signedTex(r.result)}`}$`}
          </MathText>
          {regroup && (
            <p className="text-ink-600 text-sm">
              On regroupe les positifs ({signed(r.pos)}) et les négatifs ({signed(-r.neg)}).
            </p>
          )}
          <p className="text-ink-600 text-sm">
            {frenchSpaces('Écriture simplifiée : ')}
            <MathText>{`$${simplifiedTex(terms)} = ${r.result}$`}</MathText>
          </p>
          <SendInt value={r.result} onSend={onSend} sendCost={sendCost} />
        </section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Soustraire                                                          */
/* ------------------------------------------------------------------ */

function SubMode({ onSend, sendCost }: ToolProps) {
  const [a, setA] = useState(2);
  const [b, setB] = useState(-3);
  const [view, setView] = useState<'tokens' | 'line'>('tokens');
  const r = subScenes(a, b);
  const s = useSteps(`${a},${b}`, r.scenes.length);
  const scene = r.scenes[s.step]!;
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <RelStepper label="Départ" value={a} max={9} onChange={setA} />
        <RelStepper label="On enlève" value={b} max={9} onChange={setB} />
      </div>
      <MathText className="text-ink-900 block text-xl">{`$${relTex(a)} - ${relTex(b)}$`}</MathText>
      <Segmented
        ariaLabel="Vue"
        value={view}
        options={[
          { value: 'tokens', label: 'Jetons' },
          { value: 'line', label: 'Sauts sur la droite' },
        ]}
        onChange={setView}
      />
      {view === 'tokens' ? (
        <>
          <PairFigure scene={scene} most={Math.max(...r.scenes.map(sceneWidth))} />
          <StepControls say={scene.say} {...s} testId="sub-say" />
        </>
      ) : (
        <>
          <JumpFigure terms={[a, -b]} />
          <p className="text-ink-600 text-sm">
            Sur la droite, on part de {signed(a)} et on ajoute l’opposé de {signed(b)} : un saut de{' '}
            {signed(-b)}.
          </p>
        </>
      )}
      {(s.last || view === 'line') && (
        <section
          className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
          data-testid="sub-result"
        >
          <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$${oppositeTex(a, b)}$`}</MathText>
          <p className="text-ink-600 text-sm">Soustraire un nombre, c’est ajouter son opposé.</p>
          <SendInt value={r.result} onSend={onSend} sendCost={sendCost} />
        </section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Multiplier                                                          */
/* ------------------------------------------------------------------ */

function MulMode({ onSend, sendCost }: ToolProps) {
  const [a, setA] = useState(-3);
  const [b, setB] = useState(2);
  const [signs, setSigns] = useState<Sign[]>([-1, 1, -1, -1]);
  const r = productScenes(a, b);
  const s = useSteps(`${a},${b}`, r.scenes.length);
  const scene = r.scenes[s.step]!;
  const many = productSign(signs);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <RelStepper label="Fois" value={a} max={5} onChange={setA} />
        <RelStepper label="Groupe de" value={b} max={5} onChange={setB} />
      </div>
      <MathText className="text-ink-900 block text-xl">{`$${relTex(a)} \\times ${relTex(b)}$`}</MathText>
      <BlockFigure scene={scene} />
      <StepControls say={scene.say} {...s} testId="mul-say" />
      {s.last && (
        <section
          className="border-ink-100 space-y-3 rounded-2xl border px-3 py-3"
          data-testid="mul-result"
        >
          <MathText className="text-ink-900 block text-lg">
            {`$${relTex(a)} \\times ${relTex(b)} = ${signedTex(a * b)}$`}
          </MathText>
          <SignTable a={a} b={b} />
          <p className="text-ink-600 text-sm">
            {frenchSpaces('Même règle pour un quotient : ')}
            <MathText>{'$(-12) \\div (+3) = -4$'}</MathText>.
          </p>
          <SendInt value={r.result} onSend={onSend} sendCost={sendCost} />
        </section>
      )}
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3">
        <h3 className="text-ink-900 text-sm font-bold">Signe d’un produit de plusieurs facteurs</h3>
        <div className="flex flex-wrap gap-2">
          {signs.map((sg, i) => (
            <ToggleChip
              key={i}
              on={sg < 0}
              onClick={() => setSigns((t) => t.map((x, j) => (j === i ? (-x as Sign) : x)))}
            >
              <span aria-hidden>{sg < 0 ? '(−)' : '(+)'}</span>
              <span className="sr-only">
                Facteur {i + 1} : {sg < 0 ? 'négatif' : 'positif'}
              </span>
            </ToggleChip>
          ))}
          {signs.length < 6 && (
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => setSigns((t) => [...t, -1])}
            >
              Ajouter un facteur
            </button>
          )}
          {signs.length > 2 && (
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => setSigns((t) => t.slice(0, -1))}
            >
              Retirer un facteur
            </button>
          )}
        </div>
        <Note>
          <span data-testid="sign-many">{frenchSpaces(many.say)}</span>
        </Note>
      </section>
    </>
  );
}

function SignTable({ a, b }: { a: number; b: number }) {
  const cells: Array<[Sign, Sign]> = [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ];
  const sa = Math.sign(a);
  const sb = Math.sign(b);
  return (
    <div className="grid grid-cols-2 gap-1 text-center text-sm" data-testid="sign-table">
      {cells.map(([x, y]) => {
        const on = sa === x && sb === y;
        return (
          <div
            key={`${x}${y}`}
            className={cn(
              'rounded-xl border px-2 py-1.5',
              on
                ? 'border-primary-500 bg-primary-50 text-primary-700 font-bold'
                : 'border-ink-100 text-ink-600',
            )}
          >
            ({x > 0 ? '+' : '−'}) × ({y > 0 ? '+' : '−'}) = ({x * y > 0 ? '+' : '−'})
            {on && <span className="sr-only"> : c’est ce cas</span>}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Droite graduée                                                      */
/* ------------------------------------------------------------------ */

function LineMode() {
  const [a, setA] = useState(-7);
  const [b, setB] = useState(-2);
  const [opp, setOpp] = useState(false);
  const c = compareRelatives(a, b);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="A"
          value={a}
          min={-10}
          max={10}
          step={0.5}
          decimals={1}
          onChange={setA}
        />
        <NumberField
          label="B"
          value={b}
          min={-10}
          max={10}
          step={0.5}
          decimals={1}
          onChange={setB}
        />
      </div>
      <ToggleChip on={opp} onClick={() => setOpp((o) => !o)}>
        Opposés
      </ToggleChip>
      <NumberLineFigure a={a} b={b} opp={opp} />
      <div aria-live="polite" className="space-y-2">
        <MathText className="text-ink-900 block text-center text-2xl">
          {`$${signedTex(a)} ${c.rel} ${signedTex(b)}$`}
        </MathText>
        <Note live={false}>
          <span data-testid="line-say">{frenchSpaces(c.say)}</span>
        </Note>
        <p className="text-ink-600 text-sm" data-testid="line-distance">
          {frenchSpaces(
            `Distance à zéro : ${fmt(Math.abs(a))} pour A, ${fmt(Math.abs(b))} pour B.`,
          )}
          {opp && (
            <>
              {' '}
              L’opposé de {signed(a)} est {signed(-a)}, celui de {signed(b)} est {signed(-b)} : même
              distance à zéro, de l’autre côté.
            </>
          )}
        </p>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* figures                                                             */
/* ------------------------------------------------------------------ */

/** jetons plus gros quand il y en a peu */
const tokenSize = (n: number) =>
  n <= 10 ? { T: 32, R: 13, perRow: 10 } : { T: 26, R: 10.5, perRow: 12 };

function Token({
  x,
  y,
  sign,
  R = 11,
  faded = false,
  removed = false,
  added = false,
}: {
  x: number;
  y: number;
  sign: Sign;
  R?: number;
  faded?: boolean;
  removed?: boolean;
  added?: boolean;
}) {
  return (
    <g opacity={faded || removed ? 0.25 : 1}>
      <circle cx={x} cy={y} r={R} fill={sign > 0 ? PLUS_C : MINUS_C} />
      <text
        x={x}
        y={y + R * 0.42}
        textAnchor="middle"
        fontSize={R * 1.3}
        fontWeight={800}
        fill="#fff"
      >
        {sign > 0 ? '+' : '−'}
      </text>
      {added && (
        <circle
          cx={x}
          cy={y}
          r={R + 2}
          fill="none"
          stroke={ADDED_C}
          strokeWidth={2}
          strokeDasharray="3 2"
        />
      )}
      {removed && (
        <line x1={x - R} y1={y + R} x2={x + R} y2={y - R} stroke={INK} strokeWidth={2.5} />
      )}
    </g>
  );
}

/** `most` : le plus de jetons d'un signe sur toutes les étapes (taille stable d'une étape à l'autre) */
function PairFigure({ scene, most }: { scene: PairScene; most: number }) {
  const n = sceneWidth(scene);
  const { T, R, perRow: PER_ROW } = tokenSize(most);
  const rows = Math.max(1, Math.ceil(n / PER_ROW));
  const cols = Math.min(PER_ROW, Math.max(most, 1));
  const left = (VB_W - cols * T) / 2 + T / 2;
  const rowH = 2 * T + 12;
  const H = rows * rowH + 12;
  const at = (k: number, sign: Sign) => {
    const c = k + (sign > 0 ? scene.posOffset : scene.negOffset);
    return {
      x: left + (c % PER_ROW) * T,
      y: 8 + T / 2 + Math.floor(c / PER_ROW) * rowH + (sign < 0 ? T : 0),
    };
  };
  const items: ReactElement[] = [];
  for (let k = 0; k < scene.pairs; k++) {
    const p = at(k, 1);
    items.push(
      <rect
        key={`p${k}`}
        x={p.x - T / 2 + 1}
        y={p.y - T / 2 + 1}
        width={T - 2}
        height={2 * T - 2}
        rx={10}
        fill="none"
        stroke={MUTED}
        strokeWidth={1.5}
        strokeDasharray="4 3"
        opacity={scene.cancelled ? 0.4 : 1}
      />,
    );
  }
  for (const [count, sign, removedN] of [
    [scene.pos, 1, scene.removedPos],
    [scene.neg, -1, scene.removedNeg],
  ] as const) {
    for (let k = 0; k < count; k++) {
      const p = at(k, sign);
      items.push(
        <Token
          key={`${sign}${k}`}
          {...p}
          sign={sign}
          R={R}
          faded={scene.cancelled && k < scene.pairs}
          removed={k >= count - removedN}
          added={k >= count - scene.added}
        />,
      );
    }
  }
  // ce qui compte encore : ni paire effacée, ni jeton enlevé
  const keepPos = scene.pos - scene.removedPos - (scene.cancelled ? scene.pairs : 0);
  const keepNeg = scene.neg - scene.removedNeg - (scene.cancelled ? scene.pairs : 0);
  const label =
    n === 0
      ? 'Aucun jeton.'
      : `${keepPos} jeton${keepPos > 1 ? 's' : ''} plus, ${keepNeg} jeton${keepNeg > 1 ? 's' : ''} moins${scene.pairs && !scene.cancelled ? `, dont ${scene.pairs} paire${scene.pairs > 1 ? 's' : ''} entourée${scene.pairs > 1 ? 's' : ''}` : ''}${scene.added ? `, dont ${scene.added} paire${scene.added > 1 ? 's' : ''} ajoutée${scene.added > 1 ? 's' : ''}` : ''}.`;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={label}
      data-testid="pair-figure"
      data-pos={scene.pos}
      data-neg={scene.neg}
    >
      {n === 0 && (
        <text x={VB_W / 2} y={H / 2 + 5} textAnchor="middle" fontSize={14} fill={MUTED}>
          {frenchSpaces('aucun jeton : 0')}
        </text>
      )}
      {items}
    </svg>
  );
}

function BlockFigure({ scene }: { scene: BlockScene }) {
  const S = 24;
  const gap = 34;
  const widths = scene.blocks.map((b) => b.cols * S);
  const total = widths.reduce((s, w) => s + w, 0) + gap * Math.max(0, scene.blocks.length - 1);
  const maxRows = Math.max(1, ...scene.blocks.map((b) => b.rows));
  const H = maxRows * S + 40;
  let x = (VB_W - total) / 2;
  const allAdded = scene.blocks.length > 0 && scene.blocks.every((b) => b.added);
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={scene.say}
      data-testid="block-figure"
    >
      {scene.blocks.length === 0 && (
        <text x={VB_W / 2} y={H / 2} textAnchor="middle" fontSize={14} fill={MUTED}>
          {frenchSpaces('aucun jeton : 0')}
        </text>
      )}
      {allAdded && (
        <>
          <rect
            x={x - 8}
            y={6}
            width={total + 16}
            height={maxRows * S + 8}
            rx={10}
            fill="none"
            stroke={ADDED_C}
            strokeWidth={2}
            strokeDasharray="5 3"
          />
          <HaloText
            x={VB_W / 2}
            y={maxRows * S + 32}
            textAnchor="middle"
            fontSize={12}
            fill={ADDED_C}
          >
            {frenchSpaces('paires nulles : elles valent 0')}
          </HaloText>
        </>
      )}
      {scene.blocks.map((b, bi) => {
        const x0 = x;
        x += widths[bi]! + gap;
        return (
          <g key={bi}>
            {Array.from({ length: b.rows * b.cols }, (_, k) => (
              <Token
                key={k}
                x={x0 + (k % b.cols) * S + S / 2}
                y={10 + Math.floor(k / b.cols) * S + S / 2}
                sign={b.sign}
                removed={b.removed}
              />
            ))}
            {b.removed && (
              <text
                x={x0 + widths[bi]! / 2}
                y={b.rows * S + 32}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill={INK}
              >
                enlevés
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** sauts sur la droite graduée : on part de 0, chaque terme est une flèche */
function JumpFigure({ terms }: { terms: readonly number[] }) {
  const sums = terms.reduce<number[]>((acc, t) => [...acc, acc.at(-1)! + t], [0]);
  let lo = Math.min(...sums) - 1;
  let hi = Math.max(...sums) + 1;
  if (hi - lo < 10) {
    const pad = (10 - (hi - lo)) / 2;
    lo = Math.floor(lo - pad);
    hi = Math.ceil(hi + pad);
  }
  const x0 = 20;
  const w = VB_W - 40;
  const x = (v: number) => x0 + ((v - lo) / (hi - lo)) * w;
  const levels = terms.length;
  const axisY = 30 + levels * 30;
  const H = axisY + 30;
  const tick = hi - lo > 24 ? 2 : 1;
  const result = sums.at(-1)!;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`On part de 0 ; ${terms.map((t) => `saut de ${spoken(t)}`).join(', ')} ; on arrive à ${spoken(result)}.`}
    >
      <line x1={x0} x2={x0 + w} y1={axisY} y2={axisY} stroke={MUTED} strokeWidth={1.5} />
      {Array.from({ length: Math.floor((hi - lo) / tick) + 1 }, (_, i) => {
        const v = Math.ceil(lo / tick) * tick + i * tick;
        if (v > hi) return null;
        const major = v === 0 || v % 5 === 0;
        return (
          <g key={v}>
            <line
              x1={x(v)}
              x2={x(v)}
              y1={axisY - (major ? 6 : 3)}
              y2={axisY + (major ? 6 : 3)}
              stroke={INK}
              strokeWidth={major ? 1.5 : 1}
            />
            {major && (
              <text x={x(v)} y={axisY + 20} textAnchor="middle" fontSize={12} fill={INK}>
                {signed(v).replace('+', '')}
              </text>
            )}
          </g>
        );
      })}
      {terms.map((t, i) => {
        if (t === 0) return null;
        const y = 22 + i * 30;
        const xa = x(sums[i]!);
        const xb = x(sums[i + 1]!);
        const c = t > 0 ? PLUS_C : MINUS_C;
        return (
          <g key={i}>
            <line
              x1={xa}
              x2={xa}
              y1={y}
              y2={axisY}
              stroke={MUTED}
              strokeWidth={1}
              strokeDasharray="2 3"
            />
            <line x1={xa} x2={xb} y1={y} y2={y} stroke={c} strokeWidth={2.5} />
            <polygon points={arrowHeadPoints(xa, y, xb, y, 10)} fill={c} />
            <HaloText
              x={(xa + xb) / 2}
              y={y - 6}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              fill={c}
            >
              {`(${signed(t)})`}
            </HaloText>
          </g>
        );
      })}
      <line
        x1={x(result)}
        x2={x(result)}
        y1={22 + (levels - 1) * 30}
        y2={axisY}
        stroke={INK}
        strokeWidth={1}
        strokeDasharray="2 3"
      />
      <circle cx={x(result)} cy={axisY} r={6} fill={INK} />
      <circle cx={x(0)} cy={axisY} r={4} fill="none" stroke={INK} strokeWidth={2} />
    </svg>
  );
}

const C_A = 'var(--color-primary-500)';
const C_B = 'var(--color-accent-500)';

function NumberLineFigure({ a, b, opp }: { a: number; b: number; opp: boolean }) {
  const x0 = 20;
  const w = VB_W - 40;
  const x = (v: number) => x0 + ((v + 10) / 20) * w;
  const axisY = 84;
  const H = 120;
  const close = Math.abs(x(a) - x(b)) < 56;
  const labelX = (v: number) => Math.min(VB_W - 34, Math.max(34, x(v)));
  const points = [
    { v: a, name: 'A', c: C_A, ly: 44 },
    { v: b, name: 'B', c: C_B, ly: close ? 22 : 44 },
  ];
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Droite graduée de moins 10 à 10 : A en ${spoken(a)}, B en ${spoken(b)}${opp ? ` ; leurs opposés en ${spoken(-a)} et ${spoken(-b)}` : ''}.`}
    >
      <line x1={x0 - 6} x2={x0 + w + 6} y1={axisY} y2={axisY} stroke={MUTED} strokeWidth={1.5} />
      {Array.from({ length: 21 }, (_, i) => {
        const v = i - 10;
        const major = v % 5 === 0;
        return (
          <g key={v}>
            <line
              x1={x(v)}
              x2={x(v)}
              y1={axisY - (major ? 6 : 3)}
              y2={axisY + (major ? 6 : 3)}
              stroke={INK}
              strokeWidth={major ? 1.5 : 1}
            />
            {major && (
              <text x={x(v)} y={axisY + 22} textAnchor="middle" fontSize={12} fill={INK}>
                {signed(v).replace('+', '')}
              </text>
            )}
          </g>
        );
      })}
      {opp &&
        points
          .filter((p) => p.v !== 0)
          .map((p) => {
            const xa = x(p.v);
            const xb = x(-p.v);
            const r = Math.abs(xb - xa) / 2;
            return (
              <g key={`o${p.name}`}>
                <path
                  d={`M ${xa} ${axisY} A ${r} ${Math.min(r, 34)} 0 0 ${xb > xa ? 1 : 0} ${xb} ${axisY}`}
                  fill="none"
                  stroke={p.c}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                />
                <circle
                  cx={xb}
                  cy={axisY}
                  r={6}
                  fill="var(--fig-bg, #fff)"
                  stroke={p.c}
                  strokeWidth={2}
                />
              </g>
            );
          })}
      {/* traits et points d'abord, étiquettes ensuite : leur liseré passe par-dessus les traits */}
      {points.map((p) => (
        <g key={p.name}>
          <line x1={x(p.v)} x2={x(p.v)} y1={p.ly + 4} y2={axisY - 8} stroke={p.c} strokeWidth={1} />
          <circle cx={x(p.v)} cy={axisY} r={6} fill={p.c} />
        </g>
      ))}
      {points.map((p) => (
        <HaloText
          key={`t${p.name}`}
          x={labelX(p.v)}
          y={p.ly}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill={p.c}
        >
          {`${p.name} (${signed(p.v)})`}
        </HaloText>
      ))}
    </svg>
  );
}
